import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OdooTask } from '@/types/odoo';
import { usePlanningStore } from '@/stores/planning';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { updatePlanningTask } from '@/api/planning';
import { displayProjectName } from '@/utils/format';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';

interface PlanningTaskSheetProps {
  task: OdooTask;
  installerId: number;
  /** Per-card edit right (respects the modal's read-only scoping), gated further
   *  here by the easykit read-only rule. */
  canEdit: boolean;
  onClose: () => void;
  /** Opens the shared fase text + bestanden editor (same popup the grid uses). */
  onOpenDetails: (task: OdooTask) => void;
  /** Ask the parent to open MultiDayPopup. The parent closes the sheet first so the
   *  popup (z-50) is never trapped behind the sheet backdrop (z-60). */
  onMultiDay?: () => void;
}

/**
 * Mobile bottom-sheet of actions for a planning card. Replaces the desktop card's
 * cluster of tiny inline icons — each action here is a full-width, labeled, 48px
 * row, and the destructive "Uit planning halen" is confirm-guarded so it can never
 * be hit by accident (the trash-misfire incident).
 */
export function PlanningTaskSheet({ task, installerId, canEdit, onClose, onOpenDetails, onMultiDay }: PlanningTaskSheetProps) {
  const navigate = useNavigate();
  const setActiveTask = useProjectViewStore((s) => s.setActiveTask);
  const moveTask = usePlanningStore((s) => s.moveTask);
  const projectNameMap = usePlanningStore((s) => s.projectNameMap);
  const canViewProject = usePermissionsStore((s) => s.canView('project'));

  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmUnschedule, setConfirmUnschedule] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const projectId = task.project_id ? task.project_id[0] : 0;
  const soId = task.sale_order_id ? task.sale_order_id[0] : 0;
  const isEasykit = !!task.is_easykit;
  const hasParent = !!task.parent_id;
  const editable = canEdit && !isEasykit;
  const sourceDate = task.date_deadline ? task.date_deadline.slice(0, 10) : '';

  const projectName = projectNameMap.get(soId)
    || (task.project_id ? displayProjectName(task.project_id[1], { stripTemplateSuffix: true }) : 'Geen project');

  function goToProject() {
    setActiveTask(task.id);
    navigate(`/project/${projectId}?task=${task.id}`);
  }

  async function handleReschedule(newDate: string) {
    if (!newDate || newDate === sourceDate) { setShowReschedule(false); return; }
    setBusy(true);
    try {
      await moveTask(task.id, sourceDate, installerId, newDate, installerId);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  async function handleUnschedule() {
    setBusy(true);
    const store = usePlanningStore.getState();
    const prevTasks = store.tasks;
    usePlanningStore.setState({ tasks: prevTasks.filter((t) => t.id !== task.id) });
    try {
      await updatePlanningTask(task.id, { date_deadline: '', x_studio_installateur: [] });
      onClose();
    } catch {
      usePlanningStore.setState({ tasks: prevTasks, error: 'Taak uit planning halen mislukt' });
      setBusy(false);
    }
  }

  const rowCls = 'w-full flex items-center gap-3 min-h-[52px] px-4 text-left text-[15px] text-td-navy active:bg-gray-100 disabled:opacity-40';
  const iconCls = 'w-5 h-5 text-td-muted shrink-0';

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        className="fixed inset-x-0 bottom-0 z-[61] bg-white rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] animate-in max-h-[85vh] overflow-y-auto"
        role="dialog"
        aria-label="Taakacties"
      >
        <div className="flex justify-center pt-2.5 pb-1">
          <span className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>
        <div className="px-4 pt-1 pb-3 border-b border-gray-100">
          <p className="font-bold text-td-navy leading-tight">{projectName}</p>
          <p className="text-[13px] text-td-muted mt-0.5">{task.name}{task.isInterventie && <InterventieBadge className="ml-2 align-middle" />}</p>
        </div>

        <div className="py-1 divide-y divide-gray-50">
          {canViewProject && (
            <button type="button" className={rowCls} onClick={goToProject}>
              <svg className={iconCls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              Naar project
            </button>
          )}

          <button type="button" className={rowCls} onClick={() => { onOpenDetails(task); onClose(); }}>
            <svg className={iconCls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V18a2 2 0 01-2 2z" /></svg>
            Details en beschrijving
          </button>

          {editable && !showReschedule && (
            <button type="button" className={rowCls} onClick={() => setShowReschedule(true)}>
              <svg className={iconCls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              Verplaatsen naar andere dag
            </button>
          )}
          {editable && showReschedule && (
            <div className="px-4 py-3 flex items-center gap-3">
              <svg className={iconCls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              <input
                type="date"
                defaultValue={sourceDate}
                disabled={busy}
                onChange={(e) => handleReschedule(e.target.value)}
                className="flex-1 min-h-[44px] border border-gray-300 rounded-lg px-3 text-[15px]"
              />
            </div>
          )}

          {editable && hasParent && onMultiDay && (
            <button type="button" className={rowCls} onClick={onMultiDay}>
              <svg className={iconCls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              Meerdere dagen plannen
            </button>
          )}

          {editable && !confirmUnschedule && (
            <button type="button" className={`${rowCls} !text-red-600`} onClick={() => setConfirmUnschedule(true)}>
              <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Uit planning halen
            </button>
          )}
          {editable && confirmUnschedule && (
            <div className="px-4 py-3">
              <p className="text-[14px] text-td-navy mb-3">Deze taak uit de planning halen? De taak blijft bewaard onder “Verwijderde taken”.</p>
              <div className="flex gap-2">
                <button type="button" disabled={busy} onClick={() => setConfirmUnschedule(false)} className="flex-1 min-h-[48px] rounded-lg border border-gray-300 text-td-navy font-semibold active:bg-gray-100 disabled:opacity-40">
                  Annuleren
                </button>
                <button type="button" disabled={busy} onClick={handleUnschedule} className="flex-1 min-h-[48px] rounded-lg bg-red-600 text-white font-semibold active:bg-red-700 disabled:opacity-40">
                  {busy ? 'Bezig…' : 'Uit planning halen'}
                </button>
              </div>
            </div>
          )}
        </div>

        <button type="button" onClick={onClose} className="w-full min-h-[52px] mt-1 border-t border-gray-100 text-[15px] font-semibold text-td-muted active:bg-gray-100">
          Sluiten
        </button>
      </div>
    </>
  );
}
